// template — for review by counsel. These documents are drafting templates and
// are not legal advice. Company: DataFrontier Innovations Private Limited (CIN U62091KA2024PTC184641).
const UPDATED = "8 June 2026";

export const LEGAL = {
  terms: {
    title: "Terms of Service",
    updated: UPDATED,
    intro:
      "These Terms of Service (\u201cTerms\u201d) govern your access to and use of the smritium.ai website and the hosted services offered by DataFrontier Innovations Private Limited (\u201cDataFrontier\u201d, \u201cwe\u201d, \u201cus\u201d). By accessing the site or using the services you agree to these Terms.",
    sections: [
      { h: "1. Scope and definitions", p: [
        "These Terms apply to the smritium.ai website and any hosted or paid services we make available through it (the \u201cServices\u201d).",
        "They do not govern the open-source \u201cupii\u201d software distributed under the MIT License. Your use of upii is governed solely by that license, not by these Terms.",
        "We distinguish three things: (a) the MIT-licensed open-source upii package; (b) a free-core tier of hosted functionality; and (c) paid or enterprise tiers. Where a provision applies only to a specific tier, it says so.",
      ]},
      { h: "2. Your data belongs to you", p: [
        "You retain all right, title and interest in the data you ingest, upload, or process using the Services. We claim no ownership of it.",
        "The core product is designed to process data locally on your own device or infrastructure. For those local operations, we do not receive, store, or have access to your corpus.",
        "For any hosted or enterprise component you deliberately enable, we act only as a processor of your data on your documented instructions, as described in our Data Processing Agreement.",
      ]},
      { h: "3. Acceptable use", p: [
        "You agree to use the Services lawfully and in accordance with our Acceptable Use Policy, which is incorporated into these Terms by reference.",
        "You may not use the Services to process third-party personal data without a lawful basis, to conduct unlawful surveillance, or to distribute malware or infringing content.",
      ]},
      { h: "4. Early-preview software", p: [
        "Parts of the Services are offered as an early preview. Interfaces may change, features may be added or removed, and availability is not guaranteed.",
        "Early-preview software is provided \u201cas is\u201d and \u201cas available\u201d, without warranties of any kind to the extent permitted by law.",
      ]},
      { h: "5. Intellectual property", p: [
        "The Services, the DataFrontier and Smritium marks, and all associated content (excluding your data and excluding the MIT-licensed upii package) are owned by DataFrontier or its licensors.",
        "Nothing in these Terms transfers our intellectual property to you except the limited right to use the Services as intended.",
      ]},
      { h: "6. Fees and payment", p: [
        "Free-core functionality is provided at no charge. Paid tiers are billed as described at the point of purchase. Refunds and cancellations are governed by our Refund & Cancellation Policy.",
      ]},
      { h: "7. Disclaimers and limitation of liability", p: [
        "To the maximum extent permitted by applicable law, DataFrontier disclaims all implied warranties and shall not be liable for indirect, incidental, special, or consequential damages arising from your use of the Services.",
        "Our aggregate liability for any claim relating to the Services shall not exceed the amounts you paid to us for the Services in the twelve months preceding the claim.",
      ]},
      { h: "8. Governing law and jurisdiction", p: [
        "These Terms are governed by the laws of India. The courts at Bengaluru, Karnataka shall have exclusive jurisdiction, subject to applicable law including the Digital Personal Data Protection Act, 2023 (DPDP).",
      ]},
      { h: "9. Contact", p: [
        "Questions about these Terms may be sent to legal@datafrontier.co.",
      ]},
    ],
  },

  privacy: {
    title: "Privacy Policy",
    updated: UPDATED,
    intro:
      "This Privacy Policy explains what personal data DataFrontier collects and how we handle it. The unusual and important truth first: our core product processes your data locally, and for those operations we cannot access it.",
    sections: [
      { h: "1. The short version", p: [
        "The core memory engine runs on your device. Capture, embedding, retrieval and reasoning happen locally. Your corpus is not transmitted to us for the product to work.",
        "We collect only a small amount of data, described below, relating to the website and any hosted features you choose to enable.",
      ]},
      { h: "2. What we do collect", p: [
        "Website analytics: aggregate, privacy-respecting usage metrics for smritium.ai and datafrontier.co.",
        "Contact and application forms: the name, email, organisation and message you submit so we can respond.",
        "Enterprise telemetry: only if your organisation explicitly opts in as part of an enterprise engagement, and only as documented in the applicable agreement.",
      ]},
      { h: "3. What we do not collect", p: [
        "We do not receive the contents of your local memory corpus.",
        "We do not sell personal data, and we do not use your local data to train models.",
      ]},
      { h: "4. Your rights (DPDP & GDPR)", p: [
        "Under the DPDP Act 2023 and, where applicable, the GDPR, you have rights of access, correction, erasure, and grievance redressal in respect of personal data we hold about you.",
        "To exercise these rights, contact our Grievance Officer at legal@datafrontier.co. We respond within the timelines required by applicable law.",
      ]},
      { h: "5. Retention and security", p: [
        "We retain contact-form data only as long as needed to handle your enquiry. Hosted-service data is retained per the applicable agreement.",
        "We apply appropriate technical and organisational measures to protect the limited data we hold.",
      ]},
      { h: "6. Grievance officer", p: [
        "Data-principal grievances may be addressed to the Grievance Officer, DataFrontier Innovations Private Limited, HSR Layout, Bengaluru, or by email to legal@datafrontier.co.",
      ]},
    ],
  },

  "acceptable-use": {
    title: "Acceptable Use Policy",
    updated: UPDATED,
    intro:
      "This Acceptable Use Policy sets out activities that are prohibited when using the Services and the open-source tooling we publish.",
    sections: [
      { h: "1. Lawful use only", p: [
        "You must comply with all applicable laws, including data-protection law, when using the Services.",
      ]},
      { h: "2. No unlawful surveillance", p: [
        "You may not use the Services to surveil individuals unlawfully or to process personal data about third parties without a valid lawful basis and, where required, consent.",
      ]},
      { h: "3. No abuse", p: [
        "You may not distribute malware, attempt to breach the security of the Services, or use them to infringe intellectual-property rights or to harass others.",
      ]},
      { h: "4. Consent-gating", p: [
        "The tooling is designed to gate ambient capture through explicit consent. You must not circumvent consent controls to capture data about others without authority.",
      ]},
      { h: "5. Enforcement", p: [
        "We may suspend or terminate access for violations of this policy. Report abuse to legal@datafrontier.co.",
      ]},
    ],
  },

  "data-processing": {
    title: "Data Processing Agreement",
    updated: UPDATED,
    intro:
      "This Data Processing Agreement (\u201cDPA\u201d) applies where DataFrontier processes personal data on behalf of an enterprise customer in connection with UEII or Smritium hosted components.",
    sections: [
      { h: "1. Roles", p: [
        "For local-first operation, the customer is the data fiduciary/controller and DataFrontier does not process the corpus.",
        "For any hosted component the customer enables, the customer is the controller/fiduciary and DataFrontier is the processor, acting only on documented instructions.",
      ]},
      { h: "2. Scope of processing", p: [
        "We process personal data only to provide the contracted Services and only for the duration of the engagement.",
      ]},
      { h: "3. Sub-processors", p: [
        "We maintain a list of sub-processors and provide notice of changes. Any sub-processor is bound by obligations no less protective than those in this DPA.",
      ]},
      { h: "4. Security measures", p: [
        "We implement appropriate technical and organisational measures, including access control, encryption where applicable, and tamper-evident egress auditing via the Membrane for enterprise deployments.",
      ]},
      { h: "5. International transfers", p: [
        "Where the customer selects on-soil or on-prem deployment, personal data does not leave the designated jurisdiction. Any transfer otherwise occurs only on a lawful basis under DPDP and GDPR.",
      ]},
      { h: "6. Assistance and breach notice", p: [
        "We assist the customer with data-principal requests and notify the customer without undue delay upon becoming aware of a personal-data breach affecting processed data.",
      ]},
      { h: "7. Return and deletion", p: [
        "On termination, we return or delete processed personal data as instructed, subject to legal retention requirements.",
      ]},
    ],
  },

  "open-source": {
    title: "Open Source & Licenses",
    updated: UPDATED,
    intro:
      "The UPII core is free and open-source software published on PyPI under the MIT License. This page reproduces the license and acknowledges third-party components.",
    sections: [
      { h: "1. upii — MIT License", p: [
        "MIT License. Copyright (c) 2026 DataFrontier / upii contributors.",
        "Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the \u201cSoftware\u201d), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:",
        "The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.",
        "THE SOFTWARE IS PROVIDED \u201cAS IS\u201d, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY.",
      ]},
      { h: "2. Third-party components", p: [
        "UPII builds on open-source components including sentence-transformers, LanceDB, SQLite and (optionally) Ollama. Each is used under its respective license; attributions are included with the package distribution.",
      ]},
      { h: "3. Relationship to our patents", p: [
        "Our patent activity complements, and does not replace, this open-source commitment. The UPII core remains MIT-licensed. See the Patents page for details.",
      ]},
    ],
  },

  "refund-cancellation": {
    title: "Refund & Cancellation Policy",
    updated: UPDATED,
    intro:
      "This policy applies to paid subscriptions to hosted Services purchased through smritium.ai. The open-source upii package is free and is not subject to this policy.",
    sections: [
      { h: "1. Cancellation", p: [
        "You may cancel a paid subscription at any time. Cancellation takes effect at the end of the current billing period, and you retain access until then.",
      ]},
      { h: "2. Refunds", p: [
        "Monthly plans are non-refundable for the current period once billed. For annual plans, you may request a pro-rata refund within 14 days of the initial purchase.",
        "Refunds for enterprise agreements are governed by the terms of the applicable order form.",
      ]},
      { h: "3. How to request", p: [
        "Send cancellation or refund requests to hello@datafrontier.co. Approved refunds are returned to the original payment method within a commercially reasonable time.",
      ]},
      { h: "4. Statutory rights", p: [
        "Nothing in this policy limits any rights you have under applicable Indian consumer-protection law.",
      ]},
    ],
  },

  "security-disclosure": {
    title: "Responsible Disclosure & Security Policy",
    updated: UPDATED,
    intro:
      "We welcome reports from security researchers. This policy describes our scope and the safe-harbor we extend to good-faith research.",
    sections: [
      { h: "1. Scope", p: [
        "The smritium.ai and datafrontier.co web properties, and the upii open-source package, are in scope. Third-party services we do not operate are out of scope.",
      ]},
      { h: "2. Safe harbor", p: [
        "We will not pursue legal action against researchers who act in good faith, avoid privacy violations and service disruption, and give us a reasonable opportunity to remediate before public disclosure.",
      ]},
      { h: "3. How to report", p: [
        "Email security@datafrontier.co with steps to reproduce. Please do not disclose publicly until we confirm a fix or agree a timeline with you.",
      ]},
      { h: "4. What to expect", p: [
        "We acknowledge reports promptly, keep you updated on remediation, and credit reporters who wish to be named.",
      ]},
    ],
  },

  cookies: {
    title: "Cookie Policy",
    updated: UPDATED,
    intro:
      "We take a minimal-cookie stance, and we are proud of it. This page explains the little we use.",
    sections: [
      { h: "1. Essential only, by default", p: [
        "We use only the cookies necessary to operate the website and, where you opt in, privacy-respecting analytics. We do not use advertising or cross-site tracking cookies.",
      ]},
      { h: "2. Analytics", p: [
        "Any analytics we run are aggregate and designed to avoid identifying individuals. You can decline non-essential cookies without losing access to the site.",
      ]},
      { h: "3. Managing cookies", p: [
        "You can control or delete cookies through your browser settings at any time.",
      ]},
      { h: "4. Contact", p: [
        "Questions about cookies may be sent to legal@datafrontier.co.",
      ]},
    ],
  },
};

export const LEGAL_INDEX = [
  { slug: "terms", title: "Terms of Service" },
  { slug: "privacy", title: "Privacy Policy" },
  { slug: "acceptable-use", title: "Acceptable Use Policy" },
  { slug: "data-processing", title: "Data Processing Agreement" },
  { slug: "open-source", title: "Open Source & Licenses" },
  { slug: "refund-cancellation", title: "Refund & Cancellation Policy" },
  { slug: "security-disclosure", title: "Responsible Disclosure & Security" },
  { slug: "cookies", title: "Cookie Policy" },
];
